import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Accordion, Button, Form, Icon, Input, Modal, RadioLabel, Section, SelectHierarchyLabel, ToastContext } from '@weezevent/nacre';
import { useForm } from '@weezevent/weezjs-core';
import { useTranslation } from 'react-i18next';
import useFirstRender from 'utils/helpers/useFirstRender';
import { filterRoles, isEmail, matchSearch, pushOrSplice } from 'utils/utils';

import { BusinessEntity, LegalEntity, Organization, Role, UserV1 } from 'models';

import { LoadingCard, LoadingOptions } from 'components';

import css from '../list.module.css';
import { usePermissions } from 'models/user/permissions';

const DEFAULT_PARAMS = { search: '', limit: 10, offset: 0 };

const NewUser = ({ organizationId, useCaseItems, open, onClose, user, sync, edit = false }) => {
    const { t } = useTranslation();
    const permissions = usePermissions();
    const isAdmin = permissions.isUserAdmin();
    const isDev = permissions.isUserDev();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const isFirstRender = useFirstRender();
    const [loading, setLoading] = React.useState(false);
    const [searchRole, setSearchRole] = React.useState('');
    const [searchContextValue, setSearchContextValue] = React.useState('');
    const [queryParams, setQueryParams] = React.useState({ ...DEFAULT_PARAMS });
    const [resourceKey, resourceKey_singular] = useCaseItems;

    const isApplication = useCaseItems[0] === 'applications';

    const default_form = React.useMemo(() => {
        let _form = {
            first_name: '',
            last_name: '',
            email: '',
            roles: organizationId ? [{ context: ['*', '*', organizationId], level: 2, mode: 'specific_context' }] : []
        };
        if (edit) {
            const { client_id, client_secret, user_roles, ...filteredData } = user.original_object;
            return { ..._form, ...filteredData };
        }
        if (organizationId) {
            _form.roles[0]['context'] = ['*', '*', organizationId];
        }
        return isApplication ? { application_name: '', ..._form } : _form;
    }, [organizationId, isApplication]);

    const [form, setForm, setFormObject] = useForm({ ...default_form });

    const [roles, loadingRoles] = Role.useApiModel(
        {
            organizationId,
            cache: false,
            allow_cache: false,
            launch: open
        },
        [open, organizationId]
    );

    const [organizations, loadingOrganizations] = Organization.useApiModel(
        {
            cache: false,
            allow_cache: false,
            query: queryParams,
            launch: open && !organizationId
        },
        [queryParams, open, organizationId]
    );

    const [legalEntities, loadingLegalEntity] = LegalEntity.useApiModel({
        launch: open && !organizationId
    });

    const [businessEntities, loadingBusinessEntity] = BusinessEntity.useApiModel({
        launch: open && !organizationId
    });

    const valuesOptions = (contextValues, contextValuesLoading) => {
        if (contextValuesLoading) {
            return LoadingOptions;
        }
        if (contextValues) {
            // Return roles while applying the search filter
            return contextValues
                .filter(contextValues => matchSearch([contextValues.object.name, contextValues.object.slug], searchContextValue))
                .map(contextValues => contextValues.toOption());
        }
        return [];
    };

    const [organization, loadingOrganization] = Organization.useApiModel(
        {
            id: organizationId,
            launch: open && Boolean(organizationId)
        },
        [open, organizationId]
    );

    const disabled = React.useMemo(() => {
        return (isApplication && !form.application_name) || !form.email || loading || !isEmail(form.email);
    }, [form.application_name, form.email, loading]);

    const rolesOptions = role => {
        if (!roles || loadingRoles) {
            return LoadingOptions;
        }
        if (isAdmin) {
            return roles.filter(role => matchSearch([role.object.name, role.object.slug], searchRole)).map(role => role.toOption());
        }
        if (role) {
            // Return roles while applying the search filter
            return filterRoles(role, roles, permissions)
                .filter(role => matchSearch([role.object.name, role.object.slug], searchRole))
                .map(role => role.toOption());
        }
        return [];
    };

    const contextOptions = useMemo(() => {
        return [
            {
                key: '0',
                text: t('common.legal-entity'),
                name: t('common.legal-entity'),
                value: 0,
                disabled: !permissions._roles.some(r => r.context.level === 1) && !isDev
            },
            {
                key: '1',
                text: t('common.business-entity'),
                name: t('common.business-entity'),
                value: 1,
                disabled: !permissions._roles.some(r => r.context.level === 2) && !isDev
            },
            { key: '2', text: t('common.organization'), name: t('common.organization'), value: 2 }
        ];
    }, [permissions]);

    const organizationsOptions = React.useMemo(() => {
        if (organization) {
            return [organization.toOption()];
        }
        if (loadingOrganizations) {
            return LoadingOptions;
        }
        if (!organization && !organizations) {
            return [];
        }
        if (queryParams.search) {
            return organizations.map(org => org.toOption());
        }
        return [...organizations?.map(org => org.toOption())];
    }, [organization, organizations, loadingOrganization, loadingOrganizations, LoadingOptions, organizationId]);

    const handleRolesList = value => {
        const roles = [...form.roles];
        let addition = {
            mode: 'all_organisations',
            role: null,
            level: null
        };
        if (organizationId) {
            addition['context'] = ['*', '*', organizationId];
        }
        const new_roles = pushOrSplice(roles, addition, value);
        return setForm('roles', new_roles);
    };

    const handleRole = (name, value, index) => {
        let roles = [...form.roles];

        if (organizationId) {
            roles[index][name] = ['*', '*', organizationId];
        }

        if (name === 'context') {
            roles[index][name] = ['*', '*', '*'];
            if (roles[index][name]) {
                roles[index][name][roles[index]['level']] = value;
            }
            roles[index]['role'] = null;
        } else {
            roles[index][name] = value;
        }
        setForm('roles', roles);
    };

    const handleContext = React.useCallback(
        (value, index) => {
            const roles = [...form.roles];
            roles[index] = {
                ...form.roles[index],
                level: value,
                context: [],
                role: null
            };
            setFormObject({ ...form, roles: roles });
        },
        [form]
    );

    const handleContextMode = React.useCallback(
        (value, index) => {
            const roles = [...form.roles];
            roles[index] = {
                ...form.roles[index],
                mode: value,
                level: null,
                context: [],
                role: null
            };
            setFormObject({ ...form, roles: roles });
        },
        [form]
    );

    const handleSubmit = (next = false) => {
        setLoading(true);
        let user = new UserV1({ ...form });
        user.roles = form.roles
            ?.filter(r => r)
            .map(role => {
                if (!role.context) {
                    role.context = organizationId ? ['*', '*', organizationId] : [];
                }
                return role;
            });

        if (form.roles && form.roles.some(role => !role.role)) {
            toast.error(t('sauron.applications.toast-empty-role-error'));
            setLoading(false);
            return;
        }

        if (!isApplication) {
            user.client_id = window.weezConfig.connect.clientId;
        }

        user.save({ organizationId, useCase: useCaseItems[0] })
            .then(({ response: { client_id, client_secret, username }, status }) => {
                if ([200, 201, 204].includes(status)) {
                    user.username = username;
                    let state = isApplication ? { openSecretModal: isApplication, client_id, client_secret } : {};
                    toast.success(t(`sauron.${resourceKey}.toast-${edit ? 'edit' : 'create'}-success`));

                    if (!next) {
                        onClose();
                        history.push(`/${isApplication ? 'applications' : useCaseItems[0]}/${user.id || user.username}`, state);
                    }
                }
            })
            .catch(() => {
                toast.error(t('sauron.toasts.error'));
            })
            .finally(() => {
                if (next) {
                    setFormObject({ ...default_form }, true);
                }
                setLoading(false);
                if (edit) {
                    sync();
                }
            });
    };

    const handleSearch = value => {
        setQueryParams({ ...DEFAULT_PARAMS, search: value || undefined });
    };

    if (!open) {
        return null;
    }

    return (
        <Modal
            size={'large'}
            open={open}
            allowExternalClosing={false}
            onClose={onClose}
            title={t(`sauron.${resourceKey}.${edit ? 'edit' : 'add'}-${resourceKey_singular}-title`)}
            subtitle={t(`sauron.${resourceKey}.${edit ? 'edit' : 'add'}-${resourceKey_singular}-subtitle`)}
            customFooter={
                <Button.Group>
                    <Button onClick={onClose} label={t('common.cta.cancel')} />
                    {!isApplication && <Button primary inverted onClick={() => handleSubmit(true)} disabled={disabled} label={t(`sauron.users.add-user-action`)} />}
                    <Button primary onClick={() => handleSubmit()} disabled={disabled} label={t('common.cta.save')} />
                </Button.Group>
            }
        >
            <LoadingCard loading={loadingRoles && isFirstRender}>
                {isApplication && (
                    <div className={css['user-input']}>
                        <Form.Label label={t('common.application')} />
                        <Input
                            errors={!form.application_name}
                            value={form.application_name}
                            placeholder={t('common.application')}
                            onChange={({ target: { value } }) => {
                                setForm('application_name', value);
                            }}
                        />
                    </div>
                )}
                <div className={css['user-input']}>
                    <Form.Label label={t('common.first-name')} optionalText={t('common.optional')} />
                    <Input
                        value={form.first_name}
                        placeholder={t('common.first-name')}
                        onChange={({ target: { value } }) => {
                            setForm('first_name', value);
                        }}
                    />
                </div>
                <div className={css['user-input']}>
                    <Form.Label label={t('common.last-name')} optionalText={t('common.optional')} />
                    <Input
                        value={form.last_name}
                        placeholder={t('common.last-name')}
                        onChange={({ target: { value } }) => {
                            setForm('last_name', value);
                        }}
                    />
                </div>
                <div className={css['user-input']}>
                    <Form.Label label={t('common.email')} />
                    <Input
                        errors={!isEmail(form.email)}
                        value={form.email}
                        placeholder={t('common.email')}
                        onChange={({ target: { value } }) => {
                            setForm('email', value);
                        }}
                    />
                </div>
                {!edit && (
                    <>
                        {form.roles.map((role, index) => (
                            <div key={index} className={css['user-role-accordion']}>
                                <RoleItem
                                    index={index}
                                    role={role}
                                    isAdmin={isAdmin}
                                    permissions={permissions}
                                    searchRole={searchRole}
                                    searchContextValue={searchContextValue}
                                    handleRole={handleRole}
                                    handleContext={handleContext}
                                    handleSearch={handleSearch}
                                    contextOptions={contextOptions}
                                    rolesOptions={rolesOptions}
                                    setSearchRole={setSearchRole}
                                    setSearchContextValue={setSearchContextValue}
                                    organizationId={organizationId}
                                    handleContextMode={handleContextMode}
                                    handleRolesList={handleRolesList}
                                    queryParams={queryParams}
                                    organizationsOptions={organizationsOptions}
                                    legalEntitiesOptions={valuesOptions(legalEntities, loadingLegalEntity)}
                                    businessEntitiesOptions={valuesOptions(businessEntities, loadingBusinessEntity)}
                                />
                            </div>
                        ))}
                        <hr />
                        <div className={css['user-role-toggle']} onClick={() => handleRolesList()}>
                            <Icon name="plus" />
                            <b>{t(`sauron.${resourceKey_singular}.add-role`)}</b>
                        </div>
                    </>
                )}
            </LoadingCard>
        </Modal>
    );
};

export default NewUser;

const RoleItem = ({
    index,
    role,
    contextOptions,
    rolesOptions,
    searchRole,
    setSearchRole,
    searchContextValue,
    setSearchContextValue,
    handleContextMode,
    handleRolesList,
    handleRole,
    handleContext,
    handleSearch,
    organizationId,
    queryParams,
    organizationsOptions,
    legalEntitiesOptions,
    businessEntitiesOptions
}) => {
    const { t } = useTranslation();

    const currentContextValue = React.useMemo(() => {
        if (organizationId) {
            return Number(organizationId);
        }
        if (!role.context || !role.context.length) {
            return null;
        }
        return role.context[role.level];
    }, [role.context, organizationId]);

    const valuesOptions = React.useMemo(() => {
        switch (role.level) {
            case 0:
                return legalEntitiesOptions;
            case 1:
                return businessEntitiesOptions;
            case 2:
                return organizationsOptions;
            default:
                return [];
        }
    });

    return (
        <Accordion
            openByDefault
            title={
                <div className={css['user-role-title']}>
                    <div>{`${t('common.role')} ${index + 1}`}</div>
                    <Icon name="trash" onClick={() => handleRolesList(index)} />
                </div>
            }
        >
            <div>{t('sauron.roles.select-role-option.label')}</div>
            <RadioLabel
                bordered
                inline
                id={index}
                name={'context_mode' + index}
                value="all_organisations"
                checked={role.mode === 'all_organisations'}
                onChange={() => handleContextMode('all_organisations', index)}
            >
                {t('sauron.roles.select-role-option.all-organisations')}
            </RadioLabel>
            <RadioLabel
                bordered
                inline
                id={index}
                name={'context_mode' + index}
                value="specific_context"
                checked={role.mode === 'specific_context'}
                onChange={() => handleContextMode('specific_context', index)}
            >
                {t('sauron.roles.select-role-option.specific-context')}
            </RadioLabel>
            <Section>
                {role.mode === 'specific_context' && (
                    <>
                        <SelectHierarchyLabel
                            searchable={false}
                            disabled={Boolean(organizationId)}
                            value={role.level}
                            options={contextOptions}
                            onChange={value => handleContext(value, index)}
                            label={'Context Level'}
                            placeholder={t('common.select')}
                        />

                        <SelectHierarchyLabel
                            searchable
                            disabled={role.level === null || Boolean(organizationId)}
                            options={valuesOptions}
                            value={currentContextValue}
                            externalOptions={valuesOptions}
                            onChange={value => handleRole('context', value, index)}
                            label={'Context Value'}
                            placeholder={t('common.select')}
                            placeholderSearch={t('common.search')}
                            searchValue={role.level === 2 ? queryParams.search : searchContextValue}
                            onExternalSearch={role.level === 2 ? value => handleSearch(value) : value => setSearchContextValue(value)}
                        />
                    </>
                )}

                <SelectHierarchyLabel
                    searchable
                    disabled={!currentContextValue && role.mode === 'specific_context'}
                    value={role.role}
                    options={rolesOptions(role)}
                    externalOptions={rolesOptions(role)}
                    onChange={value => handleRole('role', value, index)}
                    label={t('common.role')}
                    placeholder={t('common.select')}
                    placeholderSearch={t('common.search')}
                    searchValue={searchRole}
                    onExternalSearch={value => setSearchRole(value)}
                />
            </Section>
        </Accordion>
    );
};
